import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { fmtDuration, prepareAttachment, type Attachment, type AttachmentKind } from '../media';
import type { Post, PostLimits } from '../types';
import { Icon } from './Icon';

/**
 * Text + up to N attachments. Photos are downscaled on the device; video and
 * audio are checked against the server's size and duration limits before any
 * bytes are sent, so an over-limit clip never costs an upload.
 */
export function PostComposer({ onPosted }: { onPosted: (p: Post) => void }) {
  const [body, setBody] = useState('');
  const [items, setItems] = useState<Attachment[]>([]);
  const [limits, setLimits] = useState<PostLimits | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [picking, setPicking] = useState<AttachmentKind>('image');

  useEffect(() => { api.postLimits().then(setLimits).catch(() => {}); }, []);
  const maxMedia = limits?.maxMedia ?? 4;
  const accept: Record<AttachmentKind, string> = { image: 'image/*', video: 'video/mp4,video/quicktime', audio: 'audio/*' };

  const pick = (kind: AttachmentKind) => { setPicking(kind); setTimeout(() => fileRef.current?.click(), 0); };

  const addFiles = async (files: FileList | null) => {
    if (!files || !limits) return;
    setError(null);
    const next = [...items];
    try {
      for (const f of Array.from(files)) {
        if (next.length >= maxMedia) break;
        const kind = f.type.startsWith('video/') ? 'video' : f.type.startsWith('audio/') ? 'audio' : 'image';
        const lim = limits.media[kind];
        if (!lim.enabled) throw new Error(`${kind} uploads are not enabled`);
        next.push(await prepareAttachment(f, lim));
      }
      setItems(next);
    } catch (e: any) { setError(e.message); setItems(next); } finally { if (fileRef.current) fileRef.current.value = ''; }
  };

  const remove = (i: number) => { URL.revokeObjectURL(items[i].previewUrl); setItems(items.filter((_, j) => j !== i)); };

  const submit = async () => {
    if (!body.trim() && items.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const post = await api.createPost(body.trim(), items);
      items.forEach((a) => URL.revokeObjectURL(a.previewUrl));
      setBody('');
      setItems([]);
      onPosted(post);
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  const sec = (ms?: number) => (ms ? `${Math.round(ms / 1000)}s` : '');
  return (
    <div className="card stack">
      <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="What are you working on?" maxLength={limits?.maxBody ?? 2000} rows={2} />
      {items.length > 0 && (
        <div className="composer-thumbs">
          {items.map((a, i) => (
            <div key={i} className={`thumb ${a.kind}`}>
              {a.kind === 'image' ? <img src={a.previewUrl} alt="" /> : a.kind === 'video' ? <video src={a.previewUrl} muted playsInline /> : <div className="audio-chip"><Icon name="mic" size={28} /></div>}
              {a.durationMs !== null && <span className="dur">{fmtDuration(a.durationMs)}</span>}
              <button type="button" onClick={() => remove(i)} aria-label="Remove">✕</button>
            </div>
          ))}
        </div>
      )}
      <div className="row between">
        <div className="row" style={{ gap: 6 }}>
          <button type="button" className="btn secondary sm" disabled={items.length >= maxMedia} onClick={() => pick('image')} aria-label="Add photo"><Icon name="camera" size={22} /></button>
          {limits?.media.video.enabled && <button type="button" className="btn secondary sm" disabled={items.length >= maxMedia} onClick={() => pick('video')} title={`Up to ${sec(limits.media.video.maxDurationMs)}`} aria-label="Add video"><Icon name="video" size={22} /></button>}
          {limits?.media.audio.enabled && <button type="button" className="btn secondary sm" disabled={items.length >= maxMedia} onClick={() => pick('audio')} title={`Up to ${sec(limits.media.audio.maxDurationMs)}`} aria-label="Add audio"><Icon name="mic" size={22} /></button>}
          <span className="muted small">{items.length ? `${items.length}/${maxMedia}` : ''}</span>
        </div>
        <button className="btn sm" disabled={busy || (!body.trim() && items.length === 0)} onClick={submit}>{busy ? 'Posting…' : <><Icon name="send" size={18} /> Post</>}</button>
      </div>
      <input ref={fileRef} type="file" accept={accept[picking]} multiple={picking === 'image'} hidden onChange={(e) => addFiles(e.target.files)} />
      {limits && <div className="muted small">Photos, video up to {sec(limits.media.video.maxDurationMs)}, audio up to {Math.round((limits.media.audio.maxDurationMs ?? 0) / 60000)} min.</div>}
      {error && <div className="error">{error}</div>}
    </div>
  );
}
